import { BankOperationSingleTypes, BankOperationRelatedTypes } from 'constants/bankOperationTypes';
import { Directions } from 'constants/directions';

import { Dispatch, SetStateAction, SyntheticEvent, useEffect } from 'react';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { required, useGetList, useNotify, useRecordContext } from 'react-admin';
import { Controller, useFormContext } from 'react-hook-form';
import { Box } from 'shared/mui/Box';
import { DatePicker } from 'shared/mui/DatePicker/DatePicker/DatePicker';
import { Divider } from 'shared/mui/Divider/Divider';
import { TextField } from 'shared/mui/TextField/TextField';
import { Tooltip } from 'shared/mui/Tooltip';
import { Typography } from 'shared/mui/Typography';
import { AutocompleteInput, TextInput } from 'shared/react-admin/Inputs';
import { ReferenceInput } from 'shared/react-admin/ReferenceInputs';
import { fieldWidth } from 'themes/layout';
import { BankOperationType } from 'types/embedded/bankOperation';
import { Currency } from 'types/embedded/currency';
import { MerchantAccount } from 'types/embedded/merchantAccount';
import { getId } from 'utils';
import { cleanEmpty } from 'utils/cleanEmpty';
import { getFilteredBankOpsTypes } from 'utils/getFilteredBankOpsTypes';

import { BoxStyled } from './RequestsBankOpsForm.styled';
import { RequestsBankOpsFormRelated } from './RequestsBankOpsFormRelated';
import { RequestsBankOpsRelatedSwitcher } from './RequestsBankOpsRelatedSwitcher';

export type RequestsBankOpsFormValues = {
  id?: string;
  amountPenny: string;
  currency: string;
  description: string;
  direction: string;
  merchant: string | null;
  merchantAccount: string | null;
  provider: string | null;
  referral: string | null;
  type: string;
  related?: {
    id?: string;
    amountPenny?: string;
    currency?: string;
    direction?: string;
    merchant?: string | null;
    merchantAccount?: string | null;
    provider?: string | null;
    referral?: string | null;
  };
  dateOfOperation?: number;
};

type Props = {
  isRelated: boolean;
  setIsRelated: Dispatch<SetStateAction<boolean>>;
  isRelatedSwitcherDisabled: boolean;
  onTypeChange?: (v: string) => void;
  errors: any;
  isEdit?: boolean;
};

export const RequestsBankOpsForm: React.FC<Props> = ({
  isRelated,
  setIsRelated,
  isRelatedSwitcherDisabled,
  onTypeChange,
  isEdit,
  errors,
}) => {
  const record = useRecordContext();
  const { control, setValue, watch, clearErrors, register, unregister, setError } =
    useFormContext<RequestsBankOpsFormValues>();
  const { data: bankOpsTypes = [] } = useGetList<BankOperationType>('bank_operation_types');

  const notify = useNotify();
  const [referral, merchant, provider, merchantAccount, type] = watch([
    'referral',
    'merchant',
    'provider',
    'merchantAccount',
    'type',
  ]);

  const onSwitcherChange = (_: SyntheticEvent, checked: boolean) => {
    checked ? register('related') : unregister('related');

    const typeId = getId(type);
    if (checked) {
      setValue('direction', `/admin/directions/${Directions.Payout}`);
      setValue('related.direction', `/admin/directions/${Directions.Payment}`);
      if (typeId === BankOperationRelatedTypes.Transfer) {
        setValue('related.referral', referral);
        setValue('related.merchant', merchant);
      }
      if (typeId === BankOperationRelatedTypes.Conversion) {
        setValue('related.referral', referral);
        setValue('related.merchant', merchant);
        setValue('related.provider', provider);
      }
      if (typeId === BankOperationRelatedTypes.Conversion) {
        setValue('related.provider', provider);
      }
    }
    setIsRelated((prev) => !prev);
  };

  const onReferralChange = (value: string) => {
    if (
      [BankOperationRelatedTypes.Conversion, BankOperationRelatedTypes.Transfer].includes(
        getId(type) as BankOperationRelatedTypes,
      )
    ) {
      setValue('related.referral', value);
      setValue('related.merchant', null);
    }
    setValue('merchant', null);
    setValue('provider', null);
    setValue('merchantAccount', null);
  };

  const onMerchantChange = (value: string) => {
    if (
      [BankOperationRelatedTypes.Transfer, BankOperationRelatedTypes.Conversion].includes(
        getId(type) as BankOperationRelatedTypes,
      )
    ) {
      setValue('related.merchant', value);
    }
    setValue('provider', null);
    setValue('merchantAccount', null);
    clearErrors('related.merchant');
  };

  const onProviderChange = (value: string) => {
    if (
      [
        BankOperationRelatedTypes.Conversion,
        BankOperationRelatedTypes.ConversionReferrals,
      ].includes(getId(type) as BankOperationRelatedTypes)
    ) {
      setValue('related.provider', value);
    }
    setValue('merchantAccount', null);
  };

  useEffect(() => {
    if (!errors) {
      return;
    }
    if (errors?.length && Array.isArray(errors)) {
      return errors?.forEach((error: any) => {
        if (error?.source?.parameter) {
          setError(error?.source?.parameter, {
            type: error?.code,
            message: error?.title,
          });
        } else {
          notify(error?.title, { type: 'error' });
        }
      });
    }
    if (typeof errors === 'object') {
      const errorsArray = Object.entries(errors);
      errorsArray?.forEach((error: any) => {
        const [property, message] = error;
        setError(property, { type: 'string', message });
      });
    }
  }, [errors]);

  return (
    <>
      <BoxStyled>
        <ReferenceInput perPage={200} reference="referrals" source="referral">
          <AutocompleteInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            fullWidth
            label="Реферал"
            onChange={onReferralChange}
            optionText="name"
            sx={fieldWidth.auto}
            validate={required()}
            variant="outlined"
          />
        </ReferenceInput>
        <ReferenceInput
          filter={cleanEmpty({ referrals: referral })}
          reference="merchants"
          source="merchant"
        >
          <AutocompleteInput
            clearOnBlur={false}
            filterToQuery={(searchText: string) => ({ name: searchText })}
            fullWidth
            label="Мерчант"
            onChange={onMerchantChange}
            optionText="name"
            sx={fieldWidth.auto}
            validate={required()}
            variant="outlined"
          />
        </ReferenceInput>
        <ReferenceInput
          filter={{ merchantAccounts: { merchants: merchant } }}
          perPage={200}
          reference="providers"
          source="provider"
        >
          <AutocompleteInput
            disabled={!merchant}
            filterToQuery={(searchText: string) => ({ name: searchText })}
            fullWidth
            label="Провайдер"
            onChange={onProviderChange}
            optionText="name"
            sx={{ ...fieldWidth.auto, gridColumn: ' 3 / span 2' }}
            validate={required()}
            variant="outlined"
          />
        </ReferenceInput>
        <ReferenceInput reference="directions" source="direction">
          <AutocompleteInput
            clearOnBlur={false}
            filterToQuery={(searchText: string) => ({ name: searchText })}
            fullWidth
            label="Направлениe"
            optionText="description"
            readOnly={isRelated || getId(type) === BankOperationSingleTypes.Percentage}
            sx={fieldWidth.auto}
            validate={required()}
            variant="outlined"
          />
        </ReferenceInput>
        <AutocompleteInput
          choices={
            isEdit
              ? isRelated
                ? getFilteredBankOpsTypes(bankOpsTypes, [
                    BankOperationSingleTypes.Manual,
                    BankOperationSingleTypes.Percentage,
                  ])
                : getFilteredBankOpsTypes(bankOpsTypes, [
                    BankOperationRelatedTypes.Conversion,
                    BankOperationRelatedTypes.ConversionReferrals,
                  ])
              : getFilteredBankOpsTypes(bankOpsTypes)
          }
          clearOnBlur={false}
          fullWidth
          getOptionDisabled={(option) =>
            ![
              BankOperationSingleTypes.Manual,
              BankOperationSingleTypes.Percentage,
              BankOperationSingleTypes.Losses,
              BankOperationRelatedTypes.Conversion,
              BankOperationRelatedTypes.Transfer,
              record?.type?.value,
            ].includes(option?.value)
          }
          label="Тип банковской операции"
          onChange={onTypeChange}
          optionText="description"
          source="type"
          sx={fieldWidth.auto}
          validate={required()}
          variant="outlined"
        />
        <ReferenceInput
          disableReferenceFilter
          enableGetChoices={() => !!provider}
          filter={{ merchants: merchant, provider: provider, partner: false }}
          reference="merchant_accounts"
          source="merchantAccount"
        >
          <AutocompleteInput
            clearOnBlur={false}
            disabled={!provider}
            filterToQuery={(searchText: string) => ({ name: searchText })}
            fullWidth
            label="Счет мерчанта"
            onChange={(_, record) => {
              setValue('currency', (record as MerchantAccount)?.currency?.['@id'] || '');
            }}
            optionText="name"
            sx={fieldWidth.auto}
            validate={required()}
            variant="outlined"
          />
        </ReferenceInput>
        {!merchantAccount ? (
          <Box mt="4px" sx={{ ...fieldWidth.auto, textAlign: 'center' }} width="100%">
            <Typography color="text.secondary" fontSize={12} lineHeight="16px">
              Значение валюты автоматически принимает значение выбранного счета
            </Typography>
          </Box>
        ) : (
          <ReferenceInput name="currency" reference="currencies" source="currency">
            <AutocompleteInput
              fullWidth
              isRequired
              optionText={(record: Currency) => `${record.alphaCode}, ${record.name}`}
              readOnly
              renderInput={(params) => (
                <TextField
                  {...params}
                  InputProps={{ endAdornment: false }}
                  size="small"
                  sx={{ backgroundColor: 'rgba(250, 250, 250, 1)' }}
                  variant="outlined"
                />
              )}
              sx={fieldWidth.auto}
              variant="outlined"
            />
          </ReferenceInput>
        )}
        <TextInput
          label="Сумма"
          parse={(v) => v.replace(',', '.')}
          source="amountPenny"
          validate={[required()]}
          variant="outlined"
        />
        <Controller
          control={control}
          name="dateOfOperation"
          render={({
            field: { onChange, onBlur, value },
            fieldState: { error },
            formState: { errors },
          }) => (
            <DatePicker
              error={!!errors.dateOfOperation}
              helperText={error?.message}
              label="Дата в ПС"
              maxDate={new Date()}
              onBlur={onBlur}
              onChange={onChange}
              required
              value={value || null}
            />
          )}
          rules={{
            required: 'Обязательно для заполнения',
          }}
        />
      </BoxStyled>
      <Box alignItems="center" display="flex" mb="20px" ml="10px">
        <RequestsBankOpsRelatedSwitcher
          checked={isRelated}
          disabled={isRelatedSwitcherDisabled}
          onChange={onSwitcherChange}
          value={isRelated}
        />
        <Tooltip
          arrow
          title={
            <Typography fontSize={12}>
              Смежная банковская операция - это создание и обработка двух записей единовременно, с
              полярными “Направлениями“, то есть “Вывод“ и “Пополнение“. Доступна только с типами
              банковских операций “Конвертация”, “Конвертация между рефералами“ и “Перевод между ПС“
            </Typography>
          }
        >
          <InfoOutlinedIcon color="secondary" />
        </Tooltip>
      </Box>
      {isRelated && <RequestsBankOpsFormRelated />}
      <Divider orientation="horizontal" sx={{ marginBottom: 3, marginTop: '4px' }} />
      <TextInput
        defaultValue=""
        fullWidth
        label="Комментарий"
        multiline
        required
        source="description"
        variant="outlined"
      />
    </>
  );
};
